export const TCT_APP_TEMPLATE = `
  <header class="site-header">
    <a class="brand" href="./" aria-label="Collaborative State Demos home">
      <span class="brand__mark" aria-hidden="true">CS</span>
      <span>
        <strong>Collaborative State Demos</strong>
        <small>Public alpha · Milestone 3</small>
      </span>
    </a>
    <nav class="site-nav module-nav" aria-label="Demo modules">
      <a href="./">JSON Merge</a>
      <a href="?demo=semantic-ast">Validators + AST</a>
      <a href="?demo=tct-delivery" aria-current="page">TCT Delivery</a>
      <a href="?demo=guided-story">Guided Story</a>
      <a href="#tct-evidence">Evidence</a>
    </nav>
  </header>

  <main id="top">
    <section class="hero tct-hero" aria-labelledby="tct-hero-title">
      <div class="hero__copy">
        <p class="eyebrow">Deterministic delivery simulation · TCT Draft-03</p>
        <h1 id="tct-hero-title">Discover.<br /><em>Confirm.</em><br />Revalidate.</h1>
        <p class="hero__lede">
          Follow one publisher-selected machine representation from a generic
          index link to exact identity bytes—and see where caches can break it.
        </p>
        <div class="hero__actions">
          <a class="button button--primary" href="#tct-workbench">Open the delivery path</a>
          <a class="button button--quiet" href="./">Return to JSON merge</a>
        </div>
      </div>
      <aside class="tct-hero__path" aria-label="TCT discovery and delivery path">
        <article><span>Entry</span><strong>example.com/</strong><code>rel=&quot;index&quot;</code></article>
        <b aria-hidden="true">→</b>
        <article><span>M-Sitemap</span><strong>catalog-v2.json</strong><code>version: 2</code></article>
        <b aria-hidden="true">→</b>
        <article><span>M-URL</span><strong>article/123/llm.json</strong><code>JCS + strong ETag</code></article>
        <b aria-hidden="true">↔</b>
        <article><span>C-URL</span><strong>article/123</strong><code>rel=&quot;canonical&quot;</code></article>
      </aside>
    </section>

    <section class="boundary-note" aria-label="Simulation boundary">
      <strong>Fixed local fixtures only</strong>
      <span>No website is fetched. This is not a crawler, Deployment Doctor, conformance validator, hosting score, or IETF endorsement.</span>
    </section>

    <section class="state-workbench section" id="tct-workbench" aria-labelledby="tct-workbench-title">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Public delivery workbench</p>
          <h2 id="tct-workbench-title">Select a path. Inspect every layer.</h2>
        </div>
        <label class="scenario-picker">
          <span>Demonstrated scenario</span>
          <select id="tct-scenario-select" aria-describedby="tct-scenario-count"></select>
          <small id="tct-scenario-count"></small>
        </label>
      </div>

      <article class="scenario-explanation state-scenario-explanation" aria-live="polite">
        <div>
          <p class="eyebrow" id="tct-scenario-category"></p>
          <h3 id="tct-scenario-title"></h3>
          <p id="tct-scenario-summary"></p>
        </div>
        <div class="scenario-rule">
          <strong>Rule</strong>
          <span id="tct-scenario-rule"></span>
          <div class="draft-reference-list" id="tct-draft-links"></div>
        </div>
      </article>

      <section class="tct-path-panel" aria-label="Observed delivery stages">
        <div class="tct-stage-list">
          <article class="tct-stage" id="tct-stage-entry"><span>01</span><strong>Entry resource</strong><small id="tct-stage-entry-state"></small></article>
          <i aria-hidden="true">→</i>
          <article class="tct-stage" id="tct-stage-catalog"><span>02</span><strong>M-Sitemap</strong><small id="tct-stage-catalog-state"></small></article>
          <i aria-hidden="true">→</i>
          <article class="tct-stage" id="tct-stage-murl"><span>03</span><strong>M-URL</strong><small id="tct-stage-murl-state"></small></article>
          <i aria-hidden="true">→</i>
          <article class="tct-stage" id="tct-stage-curl"><span>04</span><strong>Canonical C-URL</strong><small id="tct-stage-curl-state"></small></article>
        </div>
        <aside class="state-outcome tct-outcome" id="tct-outcome" aria-live="polite">
          <span id="tct-layer-label"></span>
          <strong id="tct-outcome-label"></strong>
          <p id="tct-outcome-explanation"></p>
          <small id="tct-outcome-count"></small>
        </aside>
      </section>

      <section class="timeline-section" aria-labelledby="tct-timeline-title">
        <div class="timeline-section__heading">
          <p class="eyebrow">Complete local exchange</p>
          <h3 id="tct-timeline-title">Candidate → profile → representation → decision</h3>
        </div>
        <div class="state-timeline" id="tct-timeline"></div>
      </section>
    </section>

    <section class="concepts state-principles section" id="tct-principles" aria-labelledby="tct-principles-title">
      <div class="section-heading section-heading--compact">
        <div>
          <p class="eyebrow">Four rules to carry forward</p>
          <h2 id="tct-principles-title">Delivery integrity without scope inflation.</h2>
        </div>
      </div>
      <div class="concept-grid">
        <article><span>01</span><h3>Discovery is a candidate</h3><p>A generic index or alternate link becomes TCT only after the exact revision-specific profile and structure are confirmed.</p></article>
        <article><span>02</span><h3>Identity owns the hint</h3><p>Catalog hints omit HTTP quotes and always identify the identity M-URL representation—not a gzip or Brotli variant.</p></article>
        <article><span>03</span><h3>The response is authoritative</h3><p>An item hint is advisory. The validator received from the selected M-URL response governs target-resource revalidation.</p></article>
        <article><span>04</span><h3>Layers stay independent</h3><p>A public mismatch does not by itself prove whether the origin, cache integration, proxy, or CDN caused it.</p></article>
      </div>
    </section>

    <section class="evidence section" id="tct-evidence" aria-labelledby="tct-evidence-title">
      <div class="evidence__copy">
        <p class="eyebrow">Published foundation</p>
        <h2 id="tct-evidence-title">One draft, fixed bytes, bounded explanation.</h2>
        <p>
          The simulator uses locally packaged fixtures whose JCS bytes, ETags,
          Content-Digests, and gzip variant are independently checked by tests.
        </p>
      </div>
      <dl class="evidence-list">
        <div><dt>Protocol</dt><dd>draft-jurkovikj-collab-tunnel-03</dd></div>
        <div><dt>Scenarios</dt><dd id="tct-evidence-count"></dd></div>
        <div><dt>Fixture binding</dt><dd>JCS UTF-8 · SHA-256 ETag · Content-Digest · gzip</dd></div>
        <div><dt>Network behavior</dt><dd>packaged static assets only · no simulated request leaves the browser</dd></div>
      </dl>
    </section>
  </main>

  <footer class="site-footer">
    <p>Collaborative State Demos · non-normative, browser-only explanation</p>
    <div>
      <a href="https://datatracker.ietf.org/doc/draft-jurkovikj-collab-tunnel/03/" target="_blank" rel="noreferrer">TCT Draft-03</a>
      <a href="https://github.com/antunjurkovic-collab/trusted-collab-validator" target="_blank" rel="noreferrer">Standalone validator</a>
      <a href="https://github.com/antunjurkovic-collab/collaborative-state-demos" target="_blank" rel="noreferrer">Source</a>
    </div>
  </footer>
`;
